import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { useAuthStore } from '../firebase/auth';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const signIn = useAuthStore((state) => state.signIn);
  const user = useAuthStore((state) => state.user);

  useEffect(() => {
    if (user) {
      navigate('/panelam');
    }
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await signIn(email, password);
      navigate('/panelam');
    } catch (err) {
      setError('Credenciales inválidas. Por favor, verifica tus datos.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'black',
        backgroundImage: 'radial-gradient(ellipse at top, rgba(23, 23, 23, 1), rgba(0, 0, 0, 1))',
      }}
    >
      <Paper
        elevation={24}
        sx={{
          p: 4,
          width: '100%',
          maxWidth: 400,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          background: 'rgba(0, 0, 0, 0.8)',
          border: '1px solid #00ff00',
          boxShadow: '0 0 20px rgba(0, 255, 0, 0.2)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Box
          sx={{
            width: 70,
            height: 70,
            borderRadius: '50%',
            bgcolor: 'transparent',
            border: '2px solid #00ff00',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3,
            boxShadow: '0 0 15px rgba(0, 255, 0, 0.3)',
          }}
        >
          <LockIcon sx={{ 
            color: '#00ff00',
            fontSize: 40,
            filter: 'drop-shadow(0 0 8px rgba(0, 255, 0, 0.5))'
          }} />
        </Box>

        <Typography 
          component="h1" 
          variant="h4" 
          sx={{ 
            mb: 3,
            color: '#00ff00',
            textShadow: '0 0 10px rgba(0, 255, 0, 0.5)',
            fontWeight: 'bold'
          }}
        >
          Panel de Administración
        </Typography>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              width: '100%', 
              mb: 2,
              bgcolor: 'rgba(255, 0, 0, 0.1)',
              border: '1px solid #ff0000',
              '& .MuiAlert-icon': {
                color: '#ff0000'
              }
            }}
          >
            {error}
          </Alert>
        )}

        <Box 
          component="form" 
          onSubmit={handleSubmit} 
          sx={{ 
            width: '100%',
            '& .MuiTextField-root': {
              mb: 2,
              '& .MuiOutlinedInput-root': {
                color: '#00ffff',
                '& fieldset': {
                  borderColor: '#00ff00',
                },
                '&:hover fieldset': {
                  borderColor: '#00ff00',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#00ff00',
                },
              },
              '& .MuiInputLabel-root': {
                color: '#00ffff',
                '&.Mui-focused': {
                  color: '#00ff00',
                },
              },
            }
          }}
        >
          <TextField
            margin="normal"
            required
            fullWidth
            label="Correo electrónico"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            label="Contraseña"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{
              mt: 3,
              mb: 2,
              py: 1.5,
              bgcolor: '#00ff00',
              color: 'black',
              fontWeight: 'bold',
              '&:hover': {
                bgcolor: '#00cc00',
              },
              '&:disabled': {
                bgcolor: '#004400',
              }
            }}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ color: 'black' }} />
            ) : (
              'Iniciar Sesión'
            )}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default Login;